import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18n';

const SEOHelmet = () => {
    const { language } = useLanguage();
    const siteData = {
        title: language === 'vi' ? "DevenflyWeb — WordPress Studio | Website có chiến lược" : "DevenflyWeb — WordPress Studio | Websites with strategy",
        description: language === 'vi' ? "Dịch vụ phát triển, thiết kế và bảo trì WordPress chuyên nghiệp giúp doanh nghiệp phát triển trực tuyến." : "Professional WordPress development, design, and maintenance services that help businesses grow online.",
        keywords: "WordPress development, custom WordPress themes, WordPress plugins, WooCommerce development, WordPress SEO, website maintenance, WordPress migration, WordPress security, custom web development, responsive WordPress design",
        author: "DevenflyWeb",
        url: "https://devenflyweb.studio",
        image: "https://devenflyweb.studio/logo.png",
        twitterHandle: "@devenflyweb",
        type: "website",
        siteName: "DevenflyWeb WordPress Studio"
    };

    const structuredData = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "@id": `${siteData.url}/#organization`,
                "name": siteData.siteName,
                "url": siteData.url,
                "logo": {
                    "@type": "ImageObject",
                    "url": `${siteData.url}/logo.png`,
                    "width": 300,
                    "height": 100
                },
                "description": siteData.description,
                "contactPoint": {
                    "@type": "ContactPoint",
                    "telephone": "+1-555-123-4567",
                    "contactType": "customer service",
                    "areaServed": "US",
                    "availableLanguage": "English"
                },
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "123 Web Dev Street",
                    "addressLocality": "Tech City",
                    "addressRegion": "CA",
                    "postalCode": "12345",
                    "addressCountry": "US"
                },
                "sameAs": [
                    "https://facebook.com/wordpressdevservices",
                    "https://twitter.com/wpdevservices",
                    "https://www.linkedin.com/company/devenflyweb",
                    "https://instagram.com/devenflyweb"
                ]
            },
            {
                "@type": "WebSite",
                "@id": `${siteData.url}/#website`,
                "url": siteData.url,
                "name": siteData.siteName,
                "description": siteData.description,
                "publisher": {
                    "@id": `${siteData.url}/#organization`
                },
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": `${siteData.url}/search?q={search_term_string}`,
                    "query-input": "required name=search_term_string"
                }
            },
            {
                "@type": "WebPage",
                "@id": `${siteData.url}/#webpage`,
                "url": siteData.url,
                "name": siteData.title,
                "isPartOf": {
                    "@id": `${siteData.url}/#website`
                },
                "about": {
                    "@id": `${siteData.url}/#organization`
                },
                "description": siteData.description,
                "breadcrumb": {
                    "@id": `${siteData.url}/#breadcrumb`
                },
                "inLanguage": "en-US"
            },
            {
                "@type": "BreadcrumbList",
                "@id": `${siteData.url}/#breadcrumb`,
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "item": {
                            "@type": "WebPage",
                            "@id": siteData.url,
                            "name": "Home"
                        }
                    }
                ]
            },
            {
                "@type": "Service",
                "name": "DevenflyWeb WordPress Studio",
                "description": "Professional WordPress development including custom themes, plugins, e-commerce solutions, and maintenance services",
                "provider": {
                    "@id": `${siteData.url}/#organization`
                },
                "areaServed": "Worldwide",
                "serviceType": [
                    "Custom WordPress Theme Development",
                    "WordPress Plugin Development",
                    "WooCommerce Development",
                    "WordPress SEO Optimization",
                    "WordPress Maintenance",
                    "WordPress Migration"
                ]
            },
            {
                "@type": "ProfessionalService",
                "name": "DevenflyWeb WordPress Studio",
                "image": siteData.image,
                "description": siteData.description,
                "provider": {
                    "@id": `${siteData.url}/#organization`
                },
                "areaServed": "US",
                "priceRange": "$500 - $2500+",
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.9",
                    "reviewCount": "127",
                    "bestRating": "5",
                    "worstRating": "1"
                }
            }
        ]
    };

    const faqStructuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "What is WordPress and why should I choose it?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "WordPress is the world's most popular content management system, powering over 40% of all websites. It's flexible, SEO-friendly, and offers thousands of themes and plugins. WordPress is perfect for businesses of all sizes because it's cost-effective, scalable, and easy to manage once set up properly."
                }
            },
            {
                "@type": "Question",
                "name": "How long does it take to build a WordPress website?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "The timeline depends on the complexity of your project. A basic business website typically takes 2-4 weeks, while a custom e-commerce site with advanced features can take 6-12 weeks. We provide detailed timelines during our initial consultation and keep you updated throughout the development process."
                }
            },
            {
                "@type": "Question",
                "name": "Do you provide hosting services?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "While we don't provide hosting directly, we can recommend reliable hosting providers and help you set up your hosting account. We work with various hosting companies and can guide you to choose the best option based on your website's needs, traffic expectations, and budget."
                }
            }
        ]
    };

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{siteData.title}</title>
            <meta name="description" content={siteData.description} />
            <meta name="keywords" content={siteData.keywords} />
            <meta name="author" content={siteData.author} />
            <meta name="robots" content="index, follow" />
            <meta name="language" content={language === 'vi' ? 'Vietnamese' : 'English'} />
            <meta name="revisit-after" content="7 days" />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={siteData.type} />
            <meta property="og:url" content={siteData.url} />
            <meta property="og:title" content={siteData.title} />
            <meta property="og:description" content={siteData.description} />
            <meta property="og:image" content={siteData.image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content={siteData.siteName} />
            <meta property="og:locale" content={language === 'vi' ? 'vi_VN' : 'en_US'} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={siteData.url} />
            <meta property="twitter:title" content={siteData.title} />
            <meta property="twitter:description" content={siteData.description} />
            <meta property="twitter:image" content={siteData.image} />
            <meta property="twitter:site" content={siteData.twitterHandle} />
            <meta property="twitter:creator" content={siteData.twitterHandle} />

            {/* Additional Meta Tags */}
            <meta name="theme-color" content="#166ffe" />
            <meta name="msapplication-navbutton-color" content="#166ffe" />
            <meta name="apple-mobile-web-app-status-bar-style" content="#166ffe" />
            <meta name="msapplication-TileColor" content="#166ffe" />

            {/* Canonical URL */}
            <link rel="canonical" href={siteData.url} />

            {/* Preconnect to external domains for performance */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link rel="preconnect" href="https://images.unsplash.com" />

            {/* DNS Prefetch */}
            <link rel="dns-prefetch" href="//fonts.googleapis.com" />
            <link rel="dns-prefetch" href="//fonts.gstatic.com" />
            <link rel="dns-prefetch" href="//images.unsplash.com" />

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>

            {/* FAQ Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(faqStructuredData)}
            </script>

            {/* Additional SEO Meta Tags */}
            <meta name="google-site-verification" content="your-google-verification-code" />
            <meta name="msvalidate.01" content="your-bing-verification-code" />
            <meta name="yandex-verification" content="your-yandex-verification-code" />

            {/* Geo Location */}
            <meta name="geo.region" content="US-CA" />
            <meta name="geo.placename" content="Tech City, California" />
            <meta name="geo.position" content="37.7749;-122.4194" />
            <meta name="ICBM" content="37.7749, -122.4194" />

            {/* Mobile Optimization */}
            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
            <meta name="format-detection" content="telephone=yes" />
            <meta name="mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-title" content={siteData.siteName} />

            {/* Favicon and Icons */}
            <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
            <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
            <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
            <link rel="manifest" href="/site.webmanifest" />
            <link rel="icon" href="/favicon.ico" />

            {/* Performance Hints */}
            <link rel="preload" href="/fonts/inter.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />

            {/* Security Headers */}
            <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
            <meta httpEquiv="X-Frame-Options" content="DENY" />
            <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
            <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />

            {/* Content Security Policy */}
            <meta
                httpEquiv="Content-Security-Policy"
                content="default-src 'self'; img-src 'self' data: https:; script-src 'self' 'unsafe-inline' https://www.googletagmanager.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; connect-src 'self' https://www.google-analytics.com;"
            />
        </Helmet>
    );
};

export default SEOHelmet;
